import React, { useState } from "react";
import { Phone, Mail, ArrowRight, Send } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";
import { submitEnquiry } from "../api";
import { ctaImage } from "../mock";
import useSettings from "../hooks/useSettings";

const ContactCTA = () => {
  const [ref, inView] = useReveal();
  const { toast } = useToast();
  const contact = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please fill in your name and email." });
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(form);
      toast({ title: "Thank you! 🎉", description: "Our team will reach out to you shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0f2540] outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition";

  return (
    <section id="cta" className="py-20 md:py-28 bg-[#f6f9fd] overflow-hidden">
      <div ref={ref} className="pp-container grid lg:grid-cols-2 gap-12 items-center">
        {/* Left */}
        <div className={`pp-reveal ${inView ? "pp-in" : ""}`}>
          <h2 className="text-3xl md:text-[42px] font-extrabold leading-[1.14] text-[#0D1B2A]">
            Boost your success with India’s most{" "}
            <span className="pp-grad-text-orange">reliable payment APIs.</span> Get in touch.
          </h2>

          <div className="mt-8 space-y-4">
            <a href={`tel:${contact.phoneRaw}`} data-testid="cta-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-xl bg-[#0D1B2A] flex items-center justify-center group-hover:bg-[#06B6D4] transition-colors">
                <Phone size={20} className="text-white" />
              </span>
              <span>
                <span className="block text-xs text-[#64748B]">Get contact now</span>
                <span className="block font-bold text-[#0D1B2A]">{contact.phone}</span>
              </span>
            </a>
            <a href={`mailto:${contact.email}`} data-testid="cta-email" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-xl bg-[#0D1B2A] flex items-center justify-center group-hover:bg-[#06B6D4] transition-colors">
                <Mail size={20} className="text-white" />
              </span>
              <span>
                <span className="block text-xs text-[#64748B]">Send e-mail</span>
                <span className="block font-bold text-[#0D1B2A]">{contact.email}</span>
              </span>
            </a>
          </div>

          <div className="mt-8 rounded-3xl overflow-hidden shadow-xl">
            <img src={ctaImage} alt="Contact PhotonPE" className="w-full h-56 object-cover" />
          </div>
        </div>

        {/* Form */}
        <div className={`rounded-3xl bg-white p-8 md:p-10 shadow-[0_30px_60px_-30px_rgba(13,27,42,0.4)] border border-slate-100 pp-reveal ${inView ? "pp-in" : ""}`} style={{ animationDelay: "0.15s" }}>
          <h3 className="text-2xl font-extrabold text-[#0D1B2A]">Let’s Talk Business</h3>
          <p className="text-sm text-[#475569] mt-1">Fill the form and we’ll get back within 24 hours.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <input className={field} placeholder="Your Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={field} placeholder="Email Address" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className={field} placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <textarea className={field} rows={4} placeholder="Tell us about your requirement..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button
              type="submit"
              disabled={loading}
              className="group w-full flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-70"
              style={{ background: "linear-gradient(90deg, #06B6D4, #10B981)" }}
            >
              {loading ? "Sending..." : "Send Message"} <Send size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;
